/**
 * Party Dialog Helpers
 *
 * Utility functions, constants, types, and validation logic for the party dialog.
 */

import type { Address } from '@/gen/proto';
import {
  PartyType,
  GSTRegistrationType,
} from '@/gen/proto';

import { ROLE_CUSTOMER } from '../../../types/billing';
import { stringToPartyType, stringToGSTRegistrationType, partyTypeToRoles } from '../../../utils/billing-helpers';

import type { EntityAddress, EntityBankAccount } from '../../../services/entity-service';
import type { PhoneValue } from '../../ui';

// ---------------------------------------------------------------------------
// Country prefix map – used by parsePhoneString
// ---------------------------------------------------------------------------

export const PHONE_PREFIX_MAP: Record<string, string> = {
  '+1': 'US', '+7': 'RU', '+20': 'EG', '+27': 'ZA', '+30': 'GR', '+31': 'NL',
  '+32': 'BE', '+33': 'FR', '+34': 'ES', '+36': 'HU', '+39': 'IT', '+40': 'RO',
  '+41': 'CH', '+43': 'AT', '+44': 'GB', '+45': 'DK', '+46': 'SE', '+47': 'NO',
  '+48': 'PL', '+49': 'DE', '+51': 'PE', '+52': 'MX', '+53': 'CU', '+54': 'AR',
  '+55': 'BR', '+56': 'CL', '+57': 'CO', '+58': 'VE', '+60': 'MY', '+61': 'AU',
  '+62': 'ID', '+63': 'PH', '+64': 'NZ', '+65': 'SG', '+66': 'TH', '+81': 'JP',
  '+82': 'KR', '+84': 'VN', '+86': 'CN', '+90': 'TR', '+91': 'IN', '+92': 'PK',
  '+93': 'AF', '+94': 'LK', '+95': 'MM', '+98': 'IR', '+212': 'MA', '+213': 'DZ',
  '+216': 'TN', '+218': 'LY', '+220': 'GM', '+221': 'SN', '+222': 'MR',
  '+223': 'ML', '+224': 'GN', '+225': 'CI', '+226': 'BF', '+227': 'NE',
  '+228': 'TG', '+229': 'BJ', '+230': 'MU', '+231': 'LR', '+232': 'SL',
  '+233': 'GH', '+234': 'NG', '+235': 'TD', '+236': 'CF', '+237': 'CM',
  '+238': 'CV', '+239': 'ST', '+240': 'GQ', '+241': 'GA', '+242': 'CG',
  '+243': 'CD', '+244': 'AO', '+245': 'GW', '+246': 'IO', '+248': 'SC',
  '+249': 'SD', '+250': 'RW', '+251': 'ET', '+252': 'SO', '+253': 'DJ',
  '+254': 'KE', '+255': 'TZ', '+256': 'UG', '+257': 'BI', '+258': 'MZ',
  '+260': 'ZM', '+261': 'MG', '+262': 'RE', '+263': 'ZW', '+264': 'NA',
  '+265': 'MW', '+266': 'LS', '+267': 'BW', '+268': 'SZ', '+269': 'KM',
  '+290': 'SH', '+291': 'ER', '+297': 'AW', '+298': 'FO', '+299': 'GL',
  '+350': 'GI', '+351': 'PT', '+352': 'LU', '+353': 'IE', '+354': 'IS',
  '+355': 'AL', '+356': 'MT', '+357': 'CY', '+358': 'FI', '+359': 'BG',
  '+370': 'LT', '+371': 'LV', '+372': 'EE', '+373': 'MD', '+374': 'AM',
  '+375': 'BY', '+376': 'AD', '+377': 'MC', '+378': 'SM', '+380': 'UA',
  '+381': 'RS', '+382': 'ME', '+383': 'XK', '+385': 'HR', '+386': 'SI',
  '+387': 'BA', '+389': 'MK', '+420': 'CZ', '+421': 'SK', '+423': 'LI',
  '+500': 'FK', '+501': 'BZ', '+502': 'GT', '+503': 'SV', '+504': 'HN',
  '+505': 'NI', '+506': 'CR', '+507': 'PA', '+508': 'PM', '+509': 'HT',
  '+590': 'GP', '+591': 'BO', '+592': 'GY', '+593': 'EC', '+594': 'GF',
  '+595': 'PY', '+596': 'MQ', '+597': 'SR', '+598': 'UY', '+599': 'CW',
  '+670': 'TL', '+672': 'NF', '+673': 'BN', '+674': 'NR', '+675': 'PG',
  '+676': 'TO', '+677': 'SB', '+678': 'VU', '+679': 'FJ', '+680': 'PW',
  '+681': 'WF', '+682': 'CK', '+683': 'NU', '+685': 'WS', '+686': 'KI',
  '+687': 'NC', '+688': 'TV', '+689': 'PF', '+690': 'TK', '+850': 'KP',
  '+852': 'HK', '+853': 'MO', '+855': 'KH', '+856': 'LA', '+880': 'BD',
  '+886': 'TW', '+960': 'MV', '+961': 'LB', '+962': 'JO', '+963': 'SY',
  '+964': 'IQ', '+965': 'KW', '+966': 'SA', '+967': 'YE', '+968': 'OM',
  '+970': 'PS', '+971': 'AE', '+972': 'IL', '+973': 'BH', '+974': 'QA',
  '+975': 'BT', '+976': 'MN', '+977': 'NP', '+992': 'TJ', '+993': 'TM',
  '+994': 'AZ', '+995': 'GE', '+996': 'KG', '+998': 'UZ',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PartyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (party: import('@/gen/proto').Party) => void;
  orgId: string;
  ledgerId: string;
  partyType: PartyType;
  editingParty?: import('@/gen/proto').Party | null;
  existingPartyCodes?: string[];
}

export interface PendingAddress {
  type: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  stateCode: string;
  postalCode: string;
  country: string;
  countryCode: string;
  gstin: string;
  usage: string[];
}

export interface PendingBankAccount {
  accountName: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  branch: string;
  swiftCode: string;
  iban: string;
  currency: string;
}

export const EMPTY_PENDING_ADDRESS: PendingAddress = {
  type: 'office',
  line1: '',
  line2: '',
  city: '',
  state: '',
  stateCode: '',
  postalCode: '',
  country: 'India',
  countryCode: 'IN',
  gstin: '',
  usage: [],
};

export const EMPTY_PENDING_BANK: PendingBankAccount = {
  accountName: '',
  bankName: '',
  accountNumber: '',
  ifscCode: '',
  branch: '',
  swiftCode: '',
  iban: '',
  currency: 'INR',
};

export interface PartyFormData {
  entityId?: string;
  entityName: string;
  gstin?: string;
  pan?: string;
  cin?: string;
  tan?: string;
  email?: string;
  phone?: PhoneValue | null;
  mobile?: PhoneValue | null;
  website?: string;
  /** Entity addresses with usage tags (billing, shipping, consignee, …) */
  addresses: EntityAddress[];
  /** @internal Kept for geolocation effects that watch billing country code */
  billingAddress: Partial<Address>;
  billingAddressType: 'office' | 'residential' | 'warehouse';
  bankAccounts: EntityBankAccount[];
  code: string;
  tradeName?: string;
  /** @deprecated Use roles instead */
  partyType: PartyType;
  roles: string[];
  gstRegistrationType: GSTRegistrationType;
  creditDays: number;
  creditLimit: number;
  defaultCurrency: string;
  companyVendorCode?: string;
  /** Opening balance amount (only on creation, triggers subledger creation) */
  openingBalance?: number;
  /** In-progress address form — flushed to addresses[] on save */
  pendingAddress?: PendingAddress | null;
  pendingAddressOpen?: boolean;
  /** In-progress bank account form — flushed to bankAccounts[] on save */
  pendingBankAccount?: PendingBankAccount | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const EMPTY_ADDRESS: Partial<Address> = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  stateCode: '',
  postalCode: '',
  country: 'India',
  countryCode: 'IN',
};

export const EMPTY_FORM_DATA: PartyFormData = {
  entityName: '',
  gstin: '',
  pan: '',
  email: '',
  phone: null,
  mobile: null,
  addresses: [],
  billingAddress: { ...EMPTY_ADDRESS },
  billingAddressType: 'office',
  bankAccounts: [],
  pendingAddress: null,
  pendingAddressOpen: false,
  pendingBankAccount: null,
  code: '',
  partyType: stringToPartyType('customer'),
  roles: [ROLE_CUSTOMER],
  gstRegistrationType: stringToGSTRegistrationType('regular'),
  creditDays: 30,
  creditLimit: 0,
  defaultCurrency: '',
  companyVendorCode: '',
};

// State code mapping for address quick-paste parsing
export const STATE_CODE_MAP: Record<string, string> = {
  'Kerala': '32', 'Karnataka': '29', 'Tamil Nadu': '33', 'Maharashtra': '27',
  'Gujarat': '24', 'Delhi': '07', 'Rajasthan': '08', 'Punjab': '03',
  'Haryana': '06', 'Uttar Pradesh': '09', 'Madhya Pradesh': '23',
  'West Bengal': '19', 'Bihar': '10', 'Odisha': '21', 'Andhra Pradesh': '37',
  'Telangana': '36', 'Assam': '18',
};

// ---------------------------------------------------------------------------
// Phone parsing
// ---------------------------------------------------------------------------

/**
 * Parse a phone number string (international format) into PhoneValue object.
 * This handles common cases but isn't comprehensive — ideally use libphonenumber-js.
 */
export function parsePhoneString(phoneStr: string | undefined): PhoneValue | null {
  if (!phoneStr?.trim()) { return null; }

  const cleaned = phoneStr.trim();
  if (!cleaned.startsWith('+')) { return null; }

  const sortedPrefixes = Object.keys(PHONE_PREFIX_MAP).sort((a, b) => b.length - a.length);

  for (const prefix of sortedPrefixes) {
    if (cleaned.startsWith(prefix)) {
      const countryCode = PHONE_PREFIX_MAP[prefix];
      const number = cleaned.substring(prefix.length);
      return { countryCode: countryCode ?? '', telephone: prefix, number, full: cleaned };
    }
  }

  // Fallback — treat first 4 chars as prefix
  const telephone = cleaned.substring(0, Math.min(4, cleaned.length));
  const number = cleaned.substring(telephone.length);
  return { countryCode: 'IN', telephone, number, full: cleaned };
}

// ---------------------------------------------------------------------------
// Address quick-paste parser
// ---------------------------------------------------------------------------

interface ParsedAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  stateCode: string;
  postalCode: string;
}

interface LineParseAccum {
  line1: string
  line2: string
  city: string
  state: string
  stateCode: string
  postalCode: string
}

function tryParseIndianFormat(line: string, acc: LineParseAccum): boolean {
  const match = line.match(/^(.+?)\s*-\s*(\d{6})$/);
  if (!match?.[1] || !match[2] || acc.postalCode) { return false }
  const parts = match[1].split(',').map(p => p.trim());
  if (parts.length > 1) {
    acc.city = parts[parts.length - 1] ?? '';
    if (!acc.line1) { acc.line1 = parts.slice(0, -1).join(', '); }
  } else {
    acc.city = match[1];
  }
  acc.postalCode = match[2];
  return true
}

function tryParseUsFormat(line: string, acc: LineParseAccum): boolean {
  const match = line.match(/^(.+?),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (!match?.[1] || !match[2] || !match[3] || acc.postalCode) { return false }
  acc.city = match[1];
  acc.stateCode = match[2];
  acc.state = match[2];
  acc.postalCode = match[3];
  return true
}

function applyUkLocationParts(locationParts: string[], acc: LineParseAccum): void {
  if (locationParts.length >= 2) {
    acc.city = locationParts[locationParts.length - 2] ?? '';
    acc.state = locationParts[locationParts.length - 1] ?? '';
    if (locationParts.length > 2) { acc.line1 = acc.line1 || locationParts.slice(0, -2).join(' '); }
    return
  }
  if (locationParts.length === 1) {
    acc.city = locationParts[0] ?? '';
  }
}

function tryParseUkFormat(line: string, acc: LineParseAccum): boolean {
  const match = line.match(/^(.+?)\s*-\s*([A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2})/i);
  if (!match?.[1] || !match[2] || acc.postalCode) { return false }
  applyUkLocationParts(match[1].split(/\s+/).filter(Boolean), acc);
  acc.postalCode = match[2].trim();
  return true
}

function tryParseStateLine(line: string, acc: LineParseAccum): boolean {
  if (!STATE_CODE_MAP[line] || acc.state) { return false }
  acc.state = line;
  acc.stateCode = STATE_CODE_MAP[line];
  return true
}

function appendPlainLine(line: string, acc: LineParseAccum): void {
  if (!acc.line1) {
    acc.line1 = line;
  } else if (!acc.line2 && !line.includes('India') && !line.includes(acc.city)) {
    acc.line2 = line;
  }
}

/**
 * Parse a free-form address text block into structured address fields.
 * Handles Indian, US, and UK postal code formats.
 */
export function parseAddressText(text: string): ParsedAddress {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const acc: LineParseAccum = { line1: '', line2: '', city: '', state: '', stateCode: '', postalCode: '' };

  for (const line of lines) {
    if (tryParseIndianFormat(line, acc)) { continue }
    if (tryParseUsFormat(line, acc)) { continue }
    if (tryParseUkFormat(line, acc)) { continue }
    if (tryParseStateLine(line, acc)) { continue }
    appendPlainLine(line, acc);
  }

  return { ...acc };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export interface ValidationErrors {
  [key: string]: string;
}

export interface ValidateFormParams {
  formData: PartyFormData;
}

/**
 * Validate business identifiers.
 */
function validateBusinessIdentifiers(formData: PartyFormData, errors: ValidationErrors): void {
  if (formData.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstin)) {
    errors.gstin = 'Invalid GSTIN format (expected 15 characters, e.g. 29ABCDE1234F1Z5)';
  }
  if (formData.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.pan)) {
    errors.pan = 'Invalid PAN format (expected 10 characters, e.g. ABCDE1234F)';
  }
}

/**
 * Validate all form fields and return error map.
 * Returns true if form is valid (no errors).
 */
export function validatePartyForm(params: ValidateFormParams): ValidationErrors {
  const { formData } = params;
  const errors: ValidationErrors = {};

  if (!formData.entityName.trim()) { errors.entityName = 'Name is required'; }
  if (!formData.code.trim()) { errors.code = 'Code is required'; }
  if (!formData.defaultCurrency.trim()) { errors.defaultCurrency = 'Currency is required'; }
  if (!formData.roles || formData.roles.length === 0) {
    errors.roles = 'At least one role (Customer or Supplier) is required';
  }

  validateBusinessIdentifiers(formData, errors);

  if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.email = 'Invalid email address';
  }

  // Addresses are managed on the entity via the Addresses section; no form-level address validation needed

  if (formData.creditDays < 0) { errors.creditDays = 'Credit days cannot be negative'; }
  if (formData.creditLimit < 0) { errors.creditLimit = 'Credit limit cannot be negative'; }

  return errors;
}

// ---------------------------------------------------------------------------
// Title helpers
// ---------------------------------------------------------------------------

/**
 * Compute the dialog title label (Customer / Vendor / Party).
 */
export function computeTitleLabel(roles: string[]): string {
  if (roles.length === 1) {
    const SINGLE_ROLE_LABELS: Record<string, string> = {
      customer: 'Customer',
      supplier: 'Vendor',
    };
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- TODO(refactor): replace with proper null guard
    const singleRole = roles[0]!;
    return SINGLE_ROLE_LABELS[singleRole] ?? 'Party';
  }
  return 'Party';
}

// ---------------------------------------------------------------------------
// Initial form data factory
// ---------------------------------------------------------------------------

export function buildInitialFormData(
  partyType: PartyType,
  baseCurrency: string,
): PartyFormData {
  return {
    ...EMPTY_FORM_DATA,
    partyType,
    roles: partyTypeToRoles(partyType),
    defaultCurrency: baseCurrency,
  };
}
